import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import * as conversationsApi from '@/api/conversations.api';
import * as channelsApi from '@/api/channels.api';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConversationItem } from './ConversationItem';
import { HelpTip } from '@/components/ui/HelpTip';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { channelTypeLabel } from '@/lib/badges';
import { sinFiltros, textoDeResultados } from '@/lib/superfiltro';
import { FILTROS_DE_LA_BANDEJA, VALIDOS_DE_LA_BANDEJA, facetasDeLaBandeja, filtrosDePestana } from '@/lib/pestanas-bandeja';

/** Cuántos chats trae cada página. El servidor no da más de 200 por pedido. */
const POR_PAGINA = 100;

export function ConversationList({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  // SUPERFILTRO (Josué, 14-oct): bandeja, tipo y canal en UNA barra, y en la URL:
  // sobreviven al recargar y el enlace de un chat abre con la misma lista al lado.
  const { filtros, ponerVarios } = useFiltrosEnUrl(FILTROS_DE_LA_BANDEJA, VALIDOS_DE_LA_BANDEJA);

  const { data: channels } = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
  });

  const facetas = useMemo(
    () => facetasDeLaBandeja((channels ?? []).map((c) => ({ id: c.id, nombre: `${c.name} · ${channelTypeLabel[c.type]}` }))),
    [channels],
  );

  // DE A PÁGINAS, Y NO "LOS 100 PRIMEROS".
  //
  // La lista pedía 100 y ahí se quedaba, sin avisar. Esteban tenía 214 chats el
  // 22-sep-2026: 114 no aparecían en ningún lado, y lo que él pidió era
  // textual *"que yo pueda ver todos los chats realmente"*. Ahora se cargan de
  // a 100, se dice cuántos hay en total y se ofrece cargar más.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['conversations', 'list', filtros],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      conversationsApi.listConversations({
        ...filtrosDePestana(filtros.bandeja),
        channelId: filtros.canal || undefined,
        search: filtros.buscar || undefined,
        type: filtros.tipo === 'individual' || filtros.tipo === 'group' ? filtros.tipo : undefined,
        skip: pageParam,
        take: POR_PAGINA,
      }),
    getNextPageParam: (ultima, todas) => {
      const cargados = todas.reduce((n, p) => n + p.items.length, 0);
      return cargados < ultima.total ? cargados : undefined;
    },
  });
  const total = data?.pages[0]?.total ?? 0;

  // Las conversaciones en "Atención" se muestran primero para que el
  // operador las atienda sin buscarlas. `sort` es estable, así que el resto
  // conserva el orden que devuelve la API (por última actividad).
  const items = useMemo(() => {
    const list = data?.pages.flatMap((p) => p.items) ?? [];
    return [...list].sort((a, b) => {
      const aw = a.status === 'WAITING_HUMAN' ? 0 : 1;
      const bw = b.status === 'WAITING_HUMAN' ? 0 : 1;
      return aw - bw;
    });
  }, [data]);

  return (
    <div className="flex h-full w-full flex-col border-r border-surface-border bg-white dark:border-slate-800 dark:bg-slate-900 sm:w-88">
      <div className="border-b border-surface-border p-4 dark:border-slate-800">
        {/* Columna angosta: un solo botón «Filtros» con todo, también en escritorio. */}
        <Superfiltro
          compacta
          buscar={filtros.buscar}
          onBuscar={(v) => ponerVarios({ buscar: v })}
          placeholder="Buscar contacto…"
          facetas={facetas}
          valores={filtros}
          onCambiar={ponerVarios}
          onLimpiar={() => ponerVarios(sinFiltros(facetas))}
          resultados={isLoading ? undefined : textoDeResultados(total, undefined, ['chat', 'chats'])}
          acciones={<HelpTip helpKey="conversations" />}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Sin conversaciones" description="No hay conversaciones con estos filtros." className="m-4" />
        ) : (
          <ul className="divide-y divide-surface-border dark:divide-slate-800">
            {items.map((c) => (
              <li key={c.id}>
                <ConversationItem
                  conversation={c}
                  selected={selectedId === c.id}
                  onSelect={() => onSelect(c.id)}
                />
              </li>
            ))}
          </ul>
        )}
        {items.length > 0 && (
          <div className="space-y-2 p-3 text-center text-xs text-slate-500 dark:text-slate-400">
            <p>
              Mostrando {items.length} de {total}
            </p>
            {hasNextPage && (
              <button
                type="button"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                className="min-h-[40px] w-full rounded-lg border border-surface-border text-sm text-slate-700 hover:bg-surface-muted disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
              >
                {isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
